import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/**
 * Broadcast marketing email composer. Defaults to dry-run so an owner
 * can preview the audience size before paying for sends.
 */
export default function BroadcastDialog({ open, onClose, brandId }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [onlyOptIn, setOnlyOptIn] = useState(true);
  const [dry, setDry] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setSubject(''); setBody(''); setOnlyOptIn(true); setDry(true); setResult(null); }
  }, [open]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required'); return; }
    setBusy(true);
    try {
      const res = await API.broadcastBrandCustomers({
        brand_id: brandId,
        subject: subject.trim(),
        text: body,
        html: body.replace(/\n/g, '<br>'),
        only_opt_in: onlyOptIn,
        dry_run: dry,
      });
      setResult(res);
      if (dry) toast.success(`Dry run: would target ${res?.targeted || 0} customer(s)`);
      else toast.success(`Sent ${res?.sent || 0} of ${res?.targeted || 0}`);
    } catch (err) {
      toast.error(err?.message || 'Broadcast failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        Email broadcast
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <TextField
            label='Subject'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder='Spring drop is live'
            size='small'
            fullWidth
          />
          <Box>
            <TextField
              label='Message'
              multiline
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='Hi {{customer_name}}, …'
              size='small'
              fullWidth
            />
            <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
              {`{{customer_name}}`} and {`{{brand_name}}`} are substituted per recipient.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={onlyOptIn}
                onChange={(e) => setOnlyOptIn(e.target.checked)}
              />
            }
            label='Send only to customers who opted in to marketing'
          />
          <FormControlLabel
            control={
              <Switch
                checked={dry}
                onChange={(e) => setDry(e.target.checked)}
              />
            }
            label='Dry run — count recipients without sending'
          />
          {result && (
            <Box p='10px' borderRadius='10px' bgcolor='action.hover'>
              <Typography variant='body2' fontWeight='600'>
                Targeted {result.targeted || 0}
                {!dry && ` · Sent ${result.sent || 0} · Failed ${result.failed || 0}`}
              </Typography>
              {result.sample?.length > 0 && (
                <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
                  Sample: {result.sample.join(', ')}
                </Typography>
              )}
              {result.errors?.length > 0 && (
                <Box mt='6px' maxHeight='100px' overflow='auto'>
                  {result.errors.slice(0, 5).map((e, idx) => (
                    <Typography key={idx} variant='caption' color='error.main' display='block'>
                      {e.email}: {e.reason}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant='contained'
          color={dry ? 'inherit' : 'primary'}
          onClick={send}
          disabled={busy}
        >
          {busy ? 'Working…' : dry ? 'Preview audience' : 'Send broadcast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
